/**
 * Lista turni cassa con apertura e chiusura.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Clock, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

interface TurnoItem {
  id: string;
  stato: string;
  apertura_at: string;
  chiusura_at: string | null;
  fondo_cassa_iniziale: string;
  totale_contanti: string;
  totale_elettronico: string;
  scostamento: string | null;
}

const TurniList: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useFormatters();

  const [turni, setTurni] = useState<TurnoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fondoIniziale, setFondoIniziale] = useState('0.00');
  const [creating, setCreating] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/cassa/turni', { headers: authHeaders() });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: TurnoItem[] = await res.json();
      setTurni(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void carica();
  }, [carica]);

  const apriTurno = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/cassa/turni', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ fondo_cassa_iniziale: fondoIniziale }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore apertura turno');
    } finally {
      setCreating(false);
    }
  };

  const formatDateTime = (iso: string): string => fmt.dateTime(iso);

  const formatCurrency = (value: string): string => fmt.currency(value);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Turni cassa
        </h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Apertura turno */}
      <Card>
        <CardHeader>
          <CardTitle>Apri nuovo turno</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                Fondo cassa iniziale
              </label>
              <Input
                type="number"
                step="0.01"
                value={fondoIniziale}
                onChange={(e) => setFondoIniziale(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={apriTurno} disabled={creating}>
              <Plus className="mr-2 h-4 w-4" />
              Apri turno
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista turni */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Storico turni
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">Caricamento…</p>
          )}
          {!loading && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stato</TableHead>
                  <TableHead>Apertura</TableHead>
                  <TableHead>Chiusura</TableHead>
                  <TableHead className="text-right">Fondo</TableHead>
                  <TableHead className="text-right">Contanti</TableHead>
                  <TableHead className="text-right">Elettronico</TableHead>
                  <TableHead className="text-right">Scostamento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {turni.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                      Nessun turno registrato
                    </TableCell>
                  </TableRow>
                )}
                {turni.map((t: any) => (
                  <TableRow
                    key={t.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/cassa/turni/${t.id}`)}
                  >
                    <TableCell>
                      <Badge variant={t.stato === 'aperto' ? 'outline' : 'secondary'}>
                        {t.stato}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{formatDateTime(t.apertura_at)}</TableCell>
                    <TableCell className="text-sm">
                      {t.chiusura_at ? formatDateTime(t.chiusura_at) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(t.fondo_cassa_iniziale)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(t.totale_contanti)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(t.totale_elettronico)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {t.scostamento != null ? (
                        <span
                          className={
                            parseFloat(t.scostamento) !== 0
                              ? 'text-destructive'
                              : ''
                          }
                        >
                          {formatCurrency(t.scostamento)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TurniList;
